<template>
  <div class="public-view min-vh-100 d-flex align-items-center py-4">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-12 col-md-6 col-lg-5">
          <!-- LOADING -->
          <div v-if="loading" class="text-center py-5 glass-card shadow">
            <div class="spinner-border text-success" role="status"></div>
            <p class="mt-3 fw-bold text-cafe">Consultando información...</p>
          </div>

          <!-- SIN EVENTOS -->
          <div v-else-if="molde && !ultimoEvento" class="glass-card p-5 text-center">
            <div class="mb-3">
              <img src="@/assets/img/Divisan/logo_divisan.png" style="height: 40px" />
            </div>
            <h5 class="fw-bold text-cafe mb-1">{{ molde.nombre }}</h5>
            <p class="text-muted small">
              Aún no hay mantenimientos registrados para este molde.
            </p>
          </div>

          <!-- CON DATOS -->
          <div
            v-else-if="molde && ultimoEvento"
            class="card shadow-2xl border-0 rounded-4 overflow-hidden"
          >
            <!-- HEADER: logo + nombre -->
            <div class="header-molde d-flex align-items-center px-3 px-md-4 py-3">
              <img
                src="@/assets/img/Divisan/logo_divisan.png"
                class="brand-logo-img me-2 me-md-3"
              />
              <div class="flex-grow-1 text-end">
                <h6 class="small text-uppercase text-verde-oscuro fw-bold mb-1">
                  <span class="d-md-none">Molde:</span>
                  <span class="d-none d-md-inline">Mtto de Molde:</span>
                </h6>
                <h5 class="fw-black text-cafe mb-0">{{ molde.nombre }}</h5>
              </div>
            </div>

            <!-- ESTATUS DEL MOLDE -->
            <div class="estatus-wrapper">
              <div :class="['estatus-pill', clsBanner(ultimoEvento.estatus_molde)]">
                <span class="estatus-pill-icon">{{
                  iconBanner(ultimoEvento.estatus_molde)
                }}</span>
                <div class="estatus-pill-texto">
                  <span class="estatus-pill-tag">Estatus del molde</span>
                  <span class="estatus-pill-valor">{{
                    valorBanner(ultimoEvento.estatus_molde)
                  }}</span>
                </div>
                <span class="estatus-pill-desc">{{
                  descBanner(ultimoEvento.estatus_molde)
                }}</span>
              </div>
            </div>

            <!-- BODY -->
            <div class="card-body p-3 p-md-4">
              <!-- MINI CARD INFO -->
              <div class="evento-card mb-4">
                <div class="evento-item">
                  <span class="label-mini">Evento</span>
                  <span class="value-mini badge-evento">{{
                    ultimoEvento.tipo_evento
                  }}</span>
                </div>
                <div class="evento-item">
                  <span class="label-mini">Máquina</span>
                  <span class="value-mini">{{ ultimoEvento.maquina }}</span>
                </div>
                <div class="evento-item">
                  <span class="label-mini">Fecha</span>
                  <span class="value-mini">{{
                    formatFechaCorta(ultimoEvento.fecha_mantenimiento)
                  }}</span>
                </div>
                <div class="evento-item">
                  <span class="label-mini">Hora</span>
                  <span class="value-mini">{{
                    formatHora(ultimoEvento.fecha_mantenimiento)
                  }}</span>
                </div>
                <div class="evento-item">
                  <span class="label-mini">Ciclos totales</span>
                  <span class="value-mini fw-bold text-cafe">
                    {{ ultimoEvento.ciclos_totales.toLocaleString() }}
                  </span>
                </div>
              </div>

              <!-- DESCRIPCIÓN -->
              <div class="mb-4">
                <label class="label-custom">Descripción</label>
                <div class="descripcion-box">{{ ultimoEvento.descripcion }}</div>
              </div>

              <!-- REFACCIÓN -->
              <div v-if="ultimoEvento.refaccion" class="mb-4">
                <label class="label-custom">Refacción utilizada</label>
                <div class="refaccion-box">{{ ultimoEvento.refaccion }}</div>
              </div>

              <!-- CARRUSEL -->
              <div v-if="ultimoEvento.imagenes?.length">
                <label class="label-custom mb-2">Evidencias del mantenimiento</label>
                <div id="carouselMolde" class="carousel slide">
                  <div class="carousel-inner rounded-3">
                    <div
                      v-for="(img, idx) in ultimoEvento.imagenes"
                      :key="img.id"
                      :class="['carousel-item', { active: idx === 0 }]"
                    >
                      <img
                        :src="img.imagen"
                        class="d-block w-100 object-fit-cover"
                        style="height: 260px"
                      />
                    </div>
                  </div>
                  <button
                    class="carousel-control-prev"
                    type="button"
                    data-bs-target="#carouselMolde"
                    data-bs-slide="prev"
                  >
                    <span class="carousel-control-prev-icon"></span>
                  </button>
                  <button
                    class="carousel-control-next"
                    type="button"
                    data-bs-target="#carouselMolde"
                    data-bs-slide="next"
                  >
                    <span class="carousel-control-next-icon"></span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- NO ENCONTRADO -->
          <div v-else class="glass-card p-5 text-center">
            <h4 class="fw-black text-cafe">Molde no encontrado</h4>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { getMoldeUltimoMantenimiento } from "@/utils/api_backend";

const route = useRoute();
const loading = ref(true);
const molde = ref(null);
const ultimoEvento = ref(null);

async function fetchMolde() {
  try {
    loading.value = true;
    const data = await getMoldeUltimoMantenimiento(route.params.codigo);
    molde.value = data;
    ultimoEvento.value = data.mantenimientos?.[0] || null;
  } catch (error) {
    console.error("Error cargando molde:", error);
    molde.value = null;
    ultimoEvento.value = null;
  } finally {
    loading.value = false;
  }
}

function formatFechaCorta(val) {
  if (!val) return "";
  return new Date(val).toLocaleDateString("es-MX");
}
function formatHora(val) {
  if (!val) return "";
  return new Date(val).toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── Config de estatus ─────────────────────────────────────
const ESTATUS = {
  OK: {
    cls: "bloque-ok",
    icon: "✅",
    valor: "OK",
    desc: "Para usar en producción",
  },
  NO_USAR: {
    cls: "bloque-no-usar",
    icon: "🚫",
    valor: "NO USAR",
    desc: "Pendiente de mantenimiento · Molde obsoleto",
  },
  CONDICIONADO: {
    cls: "bloque-condicionado",
    icon: "⚠️",
    valor: "CONDICIONADO",
    desc: "Pendiente su validación",
  },
  COMBINADO: {
    cls: "bloque-combinado",
    icon: "🔄",
    valor: "MOLDE COMBINADO",
    desc: "Especifique número de parte",
  },
};

function clsBanner(e) {
  return ESTATUS[e]?.cls ?? "bloque-ok";
}
function iconBanner(e) {
  return ESTATUS[e]?.icon ?? "";
}
function valorBanner(e) {
  return ESTATUS[e]?.valor ?? e ?? "—";
}
function descBanner(e) {
  return ESTATUS[e]?.desc ?? "";
}

onMounted(() => {
  fetchMolde();
});
</script>

<style scoped>
/* ══ FONDO ═══════════════════════════════════════════════════ */
.public-view {
  font-family: "Inter", sans-serif;
  background: linear-gradient(rgba(20, 30, 40, 0.5), rgba(20, 30, 40, 0.4)),
    url("@/assets/img/Divisan/img_2.jpg");
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
}

/* ══ HEADER ══════════════════════════════════════════════════ */
.header-molde {
  background-color: var(--d-cream);
  border-bottom: 2px solid var(--d-green);
}
.brand-logo-img {
  height: 34px;
}
@media (min-width: 768px) {
  .brand-logo-img {
    height: 42px;
  }
}

/* ══ PÍLDORA ESTATUS ════════════════════════════════════════ */
.estatus-wrapper {
  padding: 10px 10px;
  display: flex;
  justify-content: center;
  border-bottom: 1px solid rgba(96, 108, 56, 0.15);
}

.estatus-pill {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  border-radius: 50px;
  max-width: 100%;
}

.estatus-pill-icon {
  font-size: 1.4rem;
  flex-shrink: 0;
}

.estatus-pill-texto {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.estatus-pill-tag {
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.75;
}
.estatus-pill-valor {
  font-size: 0.95rem;
  font-weight: 900;
  letter-spacing: 0.03em;
  line-height: 1;
}

.estatus-pill-desc {
  font-size: 0.72rem;
  font-weight: 600;
  opacity: 0.82;
  border-left: 1px solid rgba(255, 255, 255, 0.35);
  padding-left: 10px;
  line-height: 1.35;
}

/* Colores */
.bloque-ok {
  background: #1b5e20;
  color: #fff;
}
.bloque-no-usar {
  background: #b71c1c;
  color: #fff;
}
.bloque-condicionado {
  background: #bf360c;
  color: #fff;
}
.bloque-combinado {
  background: #37474f;
  color: #fff;
}

/* ══ CARD BODY ═══════════════════════════════════════════════ */
.evento-card {
  background: #ffffff;
  border: 1px solid rgba(96, 108, 56, 0.2);
  border-radius: 16px;
  padding: 15px;
  display: grid;
  gap: 5px;
}
.evento-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
}
.label-mini {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.7rem;
  color: var(--d-green-dark);
}
.value-mini {
  text-align: right;
}

.badge-evento {
  background-color: var(--d-green);
  color: var(--d-cream);
  padding: 6px 14px;
  border-radius: 20px;
  font-weight: 800;
  font-size: 0.75rem;
}

.label-custom {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--d-brown);
  display: block;
  margin-bottom: 6px;
}

.descripcion-box,
.refaccion-box {
  background: rgba(96, 108, 56, 0.08);
  border-left: 4px solid var(--d-green);
  padding: 10px;
  border-radius: 8px;
}

.glass-card {
  background: rgba(254, 250, 224, 0.92);
  border-radius: 20px;
  border: 1.5px solid rgba(96, 108, 56, 0.3);
  backdrop-filter: blur(8px);
}

/* ══ RESPONSIVE ══════════════════════════════════════════════ */
@media (max-width: 480px) {
  .estatus-pill {
    flex-wrap: wrap;
    border-radius: 16px;
  }
  .estatus-pill-desc {
    border-left: none;
    padding-left: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.25);
    padding-top: 6px;
    width: 100%;
  }
}
</style>
